/**
 * Share Service - Generates shareable images and handles social sharing
 */

export interface ShareableStats {
  type: 'victory' | 'high_score' | 'comeback' | 'streak';
  score: number;
  opponentScore?: number;
  handType?: string;
  winStreak?: number;
  isWinner?: boolean;
  playerName: string;
  referralCode?: string;
}

/**
 * Generate a shareable canvas image
 */
export async function generateShareableImage(stats: ShareableStats): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630; // Standard OG image size
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#0a0a1a');
  gradient.addColorStop(0.5, '#1a0a2e');
  gradient.addColorStop(1, '#0a0a1a');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Add background particles effect
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  for (let i = 0; i < 100; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    const radius = Math.random() * 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Border glow effect
  const borderGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
  borderGradient.addColorStop(0, '#00ffff');
  borderGradient.addColorStop(0.5, '#ff00ff');
  borderGradient.addColorStop(1, '#00ffff');
  ctx.strokeStyle = borderGradient;
  ctx.lineWidth = 4;
  ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

  // Title based on type
  ctx.font = 'bold 64px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';

  const titles: Record<ShareableStats['type'], { text: string; color: string }> = {
    victory: { text: 'VICTORY!', color: '#00ffff' },
    high_score: { text: 'EPIC PLAY!', color: '#ffd700' },
    comeback: { text: 'COMEBACK!', color: '#ff6b6b' },
    streak: { text: `${stats.winStreak} WIN STREAK!`, color: '#ff00ff' },
  };

  const titleConfig = titles[stats.type];
  ctx.fillStyle = titleConfig.color;
  ctx.shadowColor = titleConfig.color;
  ctx.shadowBlur = 20;
  ctx.fillText(titleConfig.text, canvas.width / 2, 120);
  ctx.shadowBlur = 0;

  // Score display
  ctx.font = 'bold 120px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(stats.score.toString(), canvas.width / 2, 280);

  if (stats.opponentScore !== undefined) {
    ctx.font = '32px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.fillText(`vs ${stats.opponentScore}`, canvas.width / 2, 330);
  }

  // Hand type if available
  if (stats.handType) {
    ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#ffd700';
    ctx.fillText(stats.handType, canvas.width / 2, 400);
  }

  // Player name
  ctx.font = '28px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fillText(`@${stats.playerName}`, canvas.width / 2, 480);

  // Solante branding
  ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#00ffff';
  ctx.fillText('SOLANTE', canvas.width / 2, 560);

  // Referral code if available
  if (stats.referralCode) {
    ctx.font = '20px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillText(`Use code: ${stats.referralCode}`, canvas.width / 2, 600);
  }

  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
}

/**
 * Share to Twitter/X
 */
export function shareToTwitter(
  text: string,
  url: string,
  hashtags: string[] = ['Solante', 'Solana', 'Web3Gaming'],
): void {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  const encodedHashtags = hashtags.join(',');
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=${encodedHashtags}`;
  window.open(twitterUrl, '_blank', 'width=600,height=400');
}

/**
 * Download shareable image
 */
export async function downloadShareableImage(stats: ShareableStats): Promise<void> {
  const blob = await generateShareableImage(stats);
  if (!blob) return;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `solante-${stats.type}-${Date.now()}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Check if this is a shareable moment based on game events
 */
export function detectShareableMoment(params: {
  score: number;
  opponentScore?: number;
  isWinner?: boolean;
  winStreak?: number;
  scoreDelta?: number;
}): ShareableStats['type'] | null {
  // High score (500+)
  if (params.score >= 500) {
    return 'high_score';
  }

  // Win with a comeback (was behind by 300+ and won)
  if (params.isWinner && params.scoreDelta && params.scoreDelta < -300) {
    return 'comeback';
  }

  // Win streak milestone
  if (
    params.winStreak &&
    (params.winStreak === 3 || params.winStreak === 5 || params.winStreak === 10)
  ) {
    return 'streak';
  }

  // Victory
  if (params.isWinner) {
    return 'victory';
  }

  return null;
}

/**
 * Generate share text based on moment type
 */
export function generateShareText(stats: ShareableStats): string {
  const texts: Record<ShareableStats['type'], string> = {
    victory: `Just won a match in Solante with ${stats.score} points! Can you beat me?`,
    high_score: `Epic ${stats.score} point play in Solante! The roguelike pvp deck-builder on Solana.`,
    comeback: `Pulled off an insane comeback in Solante! ${stats.score} vs ${stats.opponentScore}!`,
    streak: `On a ${stats.winStreak}-win streak in Solante! Who can stop me?`,
  };
  return texts[stats.type];
}
